/**
 * New formula calculations (WB and Ozon) - port from test-bench new_calc_wb.js / new_calc_oz.js.
 * Uses constants from API (new_formula_wb_*, new_formula_oz_*) and SPP/FBO data from API.
 */

import type { Constant } from '../store/slices/constantsSlice';
import type { Certificate } from '../store/slices/certificatesSlice';
import {
  calcOzonEconomics,
  isPlaceholderPurchasePercent,
  resolvePurchasePercent,
} from './ozonFullLogistic';

export { isPlaceholderPurchasePercent, resolvePurchasePercent };

export interface SppRecord {
  'пороги_wb': number | null;
  'spp_wb': number | null;
  'пороги_oz': number | null;
  'spp_oz': number | null;
}

export interface FboWbRecord {
  'Предмет': string;
  'Склад WB, %': number | null;
}

export type FboOzonData = Record<string, number>;

export interface NewFormulaInputs {
  requiredPrice: number;
  packageLength: number;
  packageWidth: number;
  packageHeight: number;
  subjectName: string;
  quantity: number;
  commissionFbo: number | null;
  certificate?: Certificate | null;
  constants: Constant[];
  sppData: SppRecord[];
  fboWbData: FboWbRecord[];
  fboOzonData: FboOzonData;
  /** Resolved purchase % (0–100). If omitted, 100. */
  purchasePercent?: number | null;
  /** Niche AVG(purchase_percent) when product pct is 0. */
  nichePurchaseAvg?: number | null;
  /** From ozon_full_logistic table; defaults embedded if missing. */
  fullLogisticBands?: { min_vol: number; max_vol: number; value: number }[];
}

export interface NewFormulaResult {
  target_price_cny: number;
  target_price_rub: number;
  target_price_cny_ozon: number;
  target_price_rub_ozon: number;
  targetWb: number;
  targetOz: number;
  target_price_rub_wb: number;
  target_price_cny_wb: number;
}

function getConst(constants: Constant[], name: string): number {
  const c = constants.find(x => x.name === name);
  return c != null ? c.value : 0;
}

function buildWbConstants(constants: Constant[]): Record<string, number> {
  const prefix = 'new_formula_wb_';
  const out: Record<string, number> = {
    yuan_exchange_rate: 11.2233,
    cost_per_vol: 700,
    customs_expenses_percent: 0,
    reserve: 0,
    advertising_budget_percent: 0.08,
    ir_markup_percent: 1.1,
    vat_percent: 0.22,
    duty_percent: 0.1,
    bv_markup_percent: 0.15,
    bv_plus_markup_percent: 0.03,
    fbo_additional_percent: 3,
    acquiring_percent: 0.02,
    profit_tax_percent: 0.25,
  };
  constants.filter(c => c.name.startsWith(prefix)).forEach(c => {
    const key = c.name.slice(prefix.length);
    out[key] = c.value;
  });
  const yuan = constants.find(c => c.name === 'yuan_exchange_rate');
  if (yuan != null) out.yuan_exchange_rate = yuan.value;
  return out;
}

function buildOzConstants(constants: Constant[]): Record<string, number> {
  const prefix = 'new_formula_oz_';
  const out: Record<string, number> = {
    average_delivery_time: 43,
    advertising_budget_percent: 0.08,
    unforeseen_expenses_percent: 0,
    customs_expenses_percent: 0,
    yuan_exchange_rate: 11.2233,
    vat_percent: 0.22,
    duty_percent: 0.116,
    ir_markup_percent: 1.3,
    bv_markup_percent: 0.15,
    bv_plus_markup_percent: 0.03,
    truck_delivery_cost: 700,
    price_cert: 49000,
    first_batch_share: 0.25,
    acquiring_percent: 0.01,
    logistic_last_mile_coefficient: 0.055,
    logistic_last_mile_min: 20,
    logistic_last_mile_max: 500,
    profit_tax_percent: 0.25,
    percent_scrap: 0.01,
    pvz_return_fee: 15,
  };
  constants.filter(c => c.name.startsWith(prefix)).forEach(c => {
    const key = c.name.slice(prefix.length);
    out[key] = c.value;
  });
  const yuan = constants.find(c => c.name === 'yuan_exchange_rate');
  if (yuan != null) out.yuan_exchange_rate = yuan.value;
  return out;
}

// --- WB ---
function givePcVol(length: number, width: number, height: number): number {
  return (length * width * height) / 1000000;
}

function getSppWb(price: number, sppData: SppRecord[]): number {
  if (!sppData?.length || !price) return 0;
  const valid: { threshold: number; value: number }[] = [];
  for (let i = 0; i < sppData.length; i++) {
    const t = sppData[i]['пороги_wb'];
    if (t != null && price > t) {
      valid.push({ threshold: t, value: sppData[i]['spp_wb'] ?? 0 });
    }
  }
  if (valid.length === 0) return 0;
  valid.sort((a, b) => a.threshold - b.threshold);
  return valid[valid.length - 1].value;
}

function wbPriceCalculate(wbPriceSpp: number, sppCoef: number): number {
  return wbPriceSpp / (1 - sppCoef / 100);
}

export function getFboWb(niche: string, fboData: FboWbRecord[]): number | null {
  if (!fboData?.length) return null;
  if (niche) {
    const match = fboData.find(r => r['Предмет'] === niche);
    if (match && match['Склад WB, %'] != null) return match['Склад WB, %'];
    const lastPart = niche.split('/').map(s => s.trim()).filter(Boolean).pop();
    if (lastPart) {
      const matchLast = fboData.find(r => r['Предмет'] === lastPart);
      if (matchLast && matchLast['Склад WB, %'] != null) return matchLast['Склад WB, %'];
    }
  }
  return null;
}

function deliveryCalculate(
  pcVol: number,
  yuanExchangeRate: number,
  costPerVol: number,
  customsExpensesPercent: number
): number {
  return (pcVol * yuanExchangeRate * costPerVol) * (1 + customsExpensesPercent);
}

function costPriceFirstWb(
  wbPrice: number,
  niche: string,
  fboData: FboWbRecord[],
  fullLogistic: number,
  reserve: number,
  advertisingBudgetPercent: number,
  irMarkupPercent: number,
  fboAdditionalPercent: number,
  acquiringPercent: number,
  fallbackFboPercent: number | null,
  profitTaxPercent: number
): number {
  let fboPercent = getFboWb(niche, fboData);
  if (fboPercent == null) {
    if (fallbackFboPercent != null) fboPercent = fallbackFboPercent;
    else throw new Error(`FBO not found for niche: ${niche}`);
  }
  const profitTaxFactor = irMarkupPercent / (1 - profitTaxPercent);
  return (
    (wbPrice * (1 - (fboPercent + fboAdditionalPercent) / 100) * (1 - reserve) -
      fullLogistic -
      wbPrice * advertisingBudgetPercent -
      wbPrice * acquiringPercent) /
    (1 + profitTaxFactor)
  );
}

function bvPriceCalculate(
  costPriceFirst: number,
  delivery: number,
  certPrice: number,
  yuanExchangeRate: number,
  vatPercent: number,
  dutyPercent: number
): number {
  return (
    (costPriceFirst - delivery) / (1 + vatPercent) / (1 + dutyPercent) -
    certPrice
  ) / yuanExchangeRate;
}

function targetCalculate(bvPrice: number, bvMarkupPercent: number): number {
  return Math.round(bvPrice * (1 - bvMarkupPercent) * 100) / 100;
}

function giveTargetWb(
  length: number,
  width: number,
  height: number,
  wbPriceSpp: number,
  sppData: SppRecord[],
  fboData: FboWbRecord[],
  constants: Record<string, number>,
  fallbackFboPercent: number | null,
  niche: string,
  certPrice: number
): { costPrice: number; yuanPrice: number } {
  const pcVol = givePcVol(length, width, height);
  const sppCoef = getSppWb(wbPriceSpp, sppData);
  const wbPrice = wbPriceCalculate(wbPriceSpp, sppCoef);
  const finalLogistic = 0;

  const costPriceFirst = costPriceFirstWb(
    wbPrice,
    niche,
    fboData,
    finalLogistic,
    constants.reserve ?? 0,
    constants.advertising_budget_percent ?? 0.08,
    constants.ir_markup_percent ?? 1.1,
    constants.fbo_additional_percent ?? 3,
    constants.acquiring_percent ?? 0.02,
    fallbackFboPercent,
    constants.profit_tax_percent ?? 0.25
  );
  const costPrice = Math.round(costPriceFirst * 100) / 100;

  const delivery = deliveryCalculate(
    pcVol,
    constants.yuan_exchange_rate ?? 11.2233,
    constants.cost_per_vol ?? 700,
    constants.customs_expenses_percent ?? 0
  );

  const bvPrice = bvPriceCalculate(
    costPriceFirst,
    delivery,
    certPrice,
    constants.yuan_exchange_rate ?? 11.2233,
    constants.vat_percent ?? 0.22,
    constants.duty_percent ?? 0.1
  );
  const target = targetCalculate(bvPrice, constants.bv_markup_percent ?? 0.15);

  return { costPrice, yuanPrice: target };
}

// --- Ozon (geometric logistics from calc_oz.py) ---
function getSppOz(price: number, sppData: SppRecord[]): number {
  if (!sppData?.length || !price) return 0;
  const valid: { threshold: number; value: number }[] = [];
  for (let i = 0; i < sppData.length; i++) {
    const t = sppData[i]['пороги_oz'];
    if (t != null && price > t) {
      valid.push({ threshold: t, value: sppData[i]['spp_oz'] ?? 0 });
    }
  }
  if (valid.length === 0) return 0;
  valid.sort((a, b) => a.threshold - b.threshold);
  return valid[valid.length - 1].value;
}

function getFboOz(ozPrice: number, fboData: FboOzonData): number {
  if (!ozPrice || !fboData) return 0;
  if (ozPrice <= 100) return fboData['FBO\nдо 100 руб.'] ?? 0;
  if (ozPrice <= 300) return fboData['FBO\nсвыше 100 \nдо 300 руб.'] ?? 0;
  if (ozPrice <= 500) return fboData['FBO\nсвыше 300 \nдо 500 руб.'] ?? 0;
  if (ozPrice <= 1500) return fboData['FBO\nсвыше 500 \nдо 1500 руб.'] ?? 0;
  return fboData['FBO\nсвыше \n1500 руб.'] ?? 0;
}

function giveTargetOzon(
  h: number,
  w: number,
  l: number,
  ozPriceSpp: number,
  sppData: SppRecord[],
  fboOzonData: FboOzonData,
  constants: Record<string, number>,
  certPriceRub: number,
  purchasePercent: number,
  fullLogisticBands?: { min_vol: number; max_vol: number; value: number }[]
): { costPrice: number; yuanPrice: number } {
  const sppCoef = getSppOz(ozPriceSpp, sppData);
  const ozPrice = sppCoef ? ozPriceSpp / (1 - sppCoef / 100) : ozPriceSpp;
  const fbo = getFboOz(ozPrice, fboOzonData);
  const result = calcOzonEconomics({
    h,
    w,
    l,
    ozPriceSpp,
    sppCoef,
    fbo,
    purchasePercent,
    certRub: certPriceRub,
    constants,
    fullLogisticBands,
  });
  return {
    costPrice: result.cost_price_rounded,
    yuanPrice: result.target_price,
  };
}

/** Check if we have enough new-formula constants to use the new calculator */
export function hasNewFormulaConstants(constants: Constant[]): boolean {
  const wb = constants.some(c => c.name.startsWith('new_formula_wb_'));
  const oz = constants.some(c => c.name.startsWith('new_formula_oz_'));
  return wb && oz;
}

/**
 * Run both WB and Ozon new formulas, return combined result with max target and Ozon values for save.
 */
export function calculateNewFormula(inputs: NewFormulaInputs): NewFormulaResult {
  const wbConst = buildWbConstants(inputs.constants);
  const ozConst = buildOzConstants(inputs.constants);
  const fallbackFbo = inputs.commissionFbo ?? null;
  const certPrice =
    inputs.certificate && inputs.quantity > 0
      ? inputs.certificate.first_batch_expenses_rub / inputs.quantity
      : 0;
  const purchasePercent = resolvePurchasePercent(
    inputs.purchasePercent,
    inputs.nichePurchaseAvg
  );

  let wbResult: { costPrice: number; yuanPrice: number };
  try {
    wbResult = giveTargetWb(
      inputs.packageLength,
      inputs.packageWidth,
      inputs.packageHeight,
      inputs.requiredPrice,
      inputs.sppData,
      inputs.fboWbData,
      wbConst,
      fallbackFbo,
      inputs.subjectName,
      certPrice
    );
  } catch (e) {
    wbResult = { costPrice: 0, yuanPrice: 0 };
  }

  let ozResult: { costPrice: number; yuanPrice: number };
  try {
    ozResult = giveTargetOzon(
      inputs.packageHeight,
      inputs.packageWidth,
      inputs.packageLength,
      inputs.requiredPrice,
      inputs.sppData,
      inputs.fboOzonData,
      ozConst,
      certPrice,
      purchasePercent,
      inputs.fullLogisticBands
    );
  } catch (e) {
    ozResult = { costPrice: 0, yuanPrice: 0 };
  }

  const targetWb = wbResult.yuanPrice;
  const targetOz = ozResult.yuanPrice;
  const yuanPrice = Math.max(targetWb, targetOz);
  const target_price_rub =
    yuanPrice === targetWb ? wbResult.costPrice : ozResult.costPrice;

  return {
    target_price_cny: yuanPrice,
    target_price_rub,
    target_price_cny_ozon: ozResult.yuanPrice,
    target_price_rub_ozon: ozResult.costPrice,
    targetWb,
    targetOz,
    target_price_rub_wb: wbResult.costPrice,
    target_price_cny_wb: wbResult.yuanPrice,
  };
}
